import React, { useState } from "react";
import { ENDPOINTS, WEB_URL } from "@/config/endpoints";
import Link from "next/link";
import dynamic from "next/dynamic";
import { FormProvider } from "@/component/shared/contactForm/formContext";
import Head from "next/head";
import { getHTSCodeSchema } from "@/lib/htsCodeSchema";
import { getHTSCodeBreadcrumbSchema } from "@/lib/htsCodesBreadcrumbSchema";

const HTSSearch = dynamic(
  () => import("@/component/shared/hts-search/htsSearch"),
  { loading: () => <p>Loading...</p> }
);
const ContactFormStep1 = dynamic(
  () => import("@/component/shared/contactForm/contactFormStep1"),
  {
    loading: () => <p>Loading...</p>,
  }
);
const ContactFormStep2 = dynamic(
  () => import("@/component/shared/contactForm/contactFormStep2"),
  {
    loading: () => <p>Loading...</p>,
  }
);

const htsCode = ({ id, chaptersData, relatedHTS }) => {
  const [showNextStep, setShowNextStep] = useState(false);

  const showSteps = () => {
    setShowNextStep(true);
  };
  const showFirstStep = () => {
    setShowNextStep(false);
  };

  function textCapitalized(value) {
    if (value != null) {
      const loweredCase = value.toLowerCase();
      return value[0].toUpperCase() + loweredCase.slice(1);
    }
  }

  const htsCodeBreadcrumbSchema = getHTSCodeBreadcrumbSchema({
    chapterTitle: chaptersData.chapter,
    chapterPageUrl: WEB_URL + `/hts-code/chapter/${chaptersData.chapter}`,
    hsnCode: id,
    hsnCodePageUrl: WEB_URL + `/hts-code/${String(id).replace(/\./g, "")}`,
    page: 'hts-code',
  });

  const htsCodeSchema = getHTSCodeSchema({
    pageMetaTitle:
      "HTS Code " +
      JSON.stringify(id).replace(/^"(.*)"$/, "$1") +
      ": " +
      chaptersData.description,
    pageUrl: WEB_URL + `/hts-code/${String(id).replace(/\./g, "")}`,
    pageMetaDesc:
      JSON.stringify(id).replace(/^"(.*)"$/, "$1") +
      " is the " +
      "HTS Code for " +
      chaptersData.description,
    hsnCode: id,
    hsnCodeDesc: chaptersData.description,
    page: 'hts-code',
  });

  return (
    <main className="mt-sp-100 sm:mt-20">
      <HTSSearch />

      <Head>
        <title>{`HTS Code ${id} - ${chaptersData.description}`}</title>
        <meta
          name="description"
          content={`HTS Code ${chaptersData.htsCode} - Understand U.S. import duty, classification & compliance for ${chaptersData.description}`}
        />
        {/* OpenGraph tags */}
        <meta
          property="og:title"
          content={
            "HTS Code " +
            JSON.stringify(id).replace(/^"(.*)"$/, "$1") +
            ": " +
            chaptersData.description
          }
        />
        <meta
          property="og:description"
          content={`HTS Code ${chaptersData.htsCode} - Understand U.S. import duty, classification & compliance for ${chaptersData.description}`}
        />
        <meta
          property="og:url"
          content={
            WEB_URL +
            "/hts-code/" +
            String(id).replace(/\./g, "")
          }
        />
        <link
          rel="canonical"
          href={
            WEB_URL +
            "/hts-code/" +
            String(id).replace(/\./g, "")
          }
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(htsCodeBreadcrumbSchema),
          }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(htsCodeSchema) }}
        />
      </Head>

      <section className="bg-white py-20">
        <div className="container sm:px-2">
          <div className="flex justify-between md:flex-col sm:flex-col gap-1">
            <div className="w-[63%] md:w-full sm:w-full pr-2 sm:pr-0 md:pr-0">
              <div className="mb-5">
                <p className="text-sm text-secondary mb-2">
                  <Link href={WEB_URL} rel="noopener noreferrer">
                    <span className="text-[#2369d8] cursor-pointer font-sm">
                      {" "}
                      Home {"  "}
                    </span>
                  </Link>
                  {"  "}
                  {" > "}
                  <Link href={WEB_URL + `/hts-code`} rel="noopener noreferrer">
                    <span className="text-[#2369d8] cursor-pointer font-sm">
                      {" "}
                      HTS Codes {"  "}
                    </span>
                  </Link>
                  {">"}
                  <Link
                    href={WEB_URL + `/hts-code/chapter/${chaptersData.chapter}`}
                    rel="noopener noreferrer"
                  >
                    <span className="text-[#2369d8] cursor-pointer font-sm">
                      {"  "}
                      {"Chapter"} {chaptersData.chapter}{" "}
                    </span>
                  </Link>{" "}
                  {id.length > 4 && (
                    <>
                      {" > "}
                      <Link
                        href={WEB_URL + `/hts-code/${id.slice(0, 4)}`}
                        rel="noopener noreferrer"
                      >
                        <span className="text-[#2369d8] cursor-pointer font-sm">
                          {"  "}
                          {id.slice(0, 4)}{" "}
                        </span>
                      </Link>
                    </>
                  )}{" "}
                  {" > "} {id}
                </p>
                <h1 className="text-base text-secondary">
                {`Custom Duty Rate for HTS Code ${chaptersData.htsCode} : ${chaptersData.description}`}
                </h1>
              </div>
              <div className="sm:overflow-x-auto flex flex-wrap rounded-sm">
                <table className="min-w-full border border-black border-collapse sm:min-w-[320px] sm:max-w-[320px] sm:w-[320px]">
                  <thead>
                    <tr className="bg-[#B5B5B5]">
                      <th
                        scope="col"
                        rowSpan="3"
                        className="py-3.5 px-1 text-left text-sm font-semibold text-gray-900 w-[8%] border border-black sm:p-xsp sm:text-xs sm:w-11 sm:max-w-11 sm:break-words"
                      >
                        Heading/Subheading
                      </th>
                      <th
                        scope="col"
                        rowSpan="3"
                        className="px-1 py-3 text-left text-sm font-semibold text-gray-900 w-[20%] border border-black sm:p-xsp sm:text-xs"
                      >
                        Article Description
                      </th>
                      <th
                        scope="col"
                        rowSpan="3"
                        className="px-2.5 py-3 text-left text-sm font-semibold text-gray-900 w-[13%] border border-black sm:p-xsp sm:text-xs sm:w-6"
                      >
                        Unit of Quantity
                      </th>
                      <th
                        colSpan="3"
                        className="border border-black text-sm font-semibold text-gray-900 text-center sm:p-1 sm:text-xsp sm:text-xs"
                      >
                        Rates of Duty
                      </th>
                    </tr>
                    <tr className="bg-[#B5B5B5]">
                      <th
                        colSpan="2"
                        className="border border-black text-sm font-semibold text-gray-900 text-center sm:p-1 sm:text-xs"
                      >
                        1
                      </th>
                      <th
                        rowSpan="2"
                        className="border border-black text-sm font-semibold text-gray-900 text-center w-[8%] sm:p-xsp sm:text-xs sm:w-6"
                      >
                        2
                      </th>
                    </tr>
                    <tr className="bg-[#B5B5B5]">
                      <th className="border border-black text-sm font-semibold text-gray-900 text-center w-[8%] sm:p-xsp sm:text-xs">
                        General
                      </th>
                      <th className="border border-black text-sm font-semibold text-gray-900 text-center w-[8%] sm:p-1 sm:text-xs">
                        Special
                      </th>
                    </tr>
                  </thead>

                  <tbody className="bg-white">
                    {chaptersData.relatedHTSCodes.length > 0 && 
                      chaptersData.relatedHTSCodes.some((hts) => hts.htsCode !== null && hts.htsCode !== undefined) ? (
                      chaptersData.relatedHTSCodes.map((hts) => (
                        <tr key={hts.htsCode} className="odd:bg-[#F5F9FC]">
                          <td className="whitespace-nowrap py-4 px-1 text-sm font-medium text-gray-900 border border-black sm:p-xsp sm:text-xs sm:w-12 sm:break-words">
                            <Link
                              className="text-anchor-blue hover:text-yellowcolor hover:underline"
                              href={WEB_URL + `/hts-code/${String(hts.htsCode).replace(/\./g, "")}`}
                            >
                              {hts.htsCode}
                            </Link>
                          </td>
                          <td className="whitespace-pre-wrap px-1 py-4 text-sm text-gray-500 border border-black sm:p-xsp sm:text-xs sm:w-12">
                            {textCapitalized(hts.description)}
                          </td>
                          <td className="whitespace-nowrap px-1 py-4 text-sm text-gray-500 border border-black sm:p-xsp sm:text-xs sm:w-6">
                            {hts.unitOfQty}
                          </td>
                          <td className="px-1 py-1 text-sm text-gray-500 border border-black text-center sm:p-xsp sm:text-xs sm:w-6">
                            {hts.generalRateOfDuty}
                          </td>
                          <td className="px-1 py-1 text-sm text-gray-500 border border-black text-center max-w-[135px] break-words sm:p-xsp sm:text-xs sm:max-w-12">
                            {hts.specialRateOfDuty}
                          </td>
                          <td className="px-1 py-1 text-sm text-gray-500 border border-black text-center sm:p-xsp sm:text-xs sm:w-12 break-words">
                            {hts.column2}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr className="odd:bg-[#F5F9FC]">
                        <td className="whitespace-nowrap py-4 px-1 text-sm font-medium text-gray-900 border border-black sm:p-xsp sm:text-xs sm:w-12 sm:break-words">
                          {chaptersData.htsCode}
                        </td>
                        <td className="whitespace-pre-wrap px-1 py-4 text-sm text-gray-500 border border-black sm:p-xsp sm:text-xs sm:w-12">
                          {textCapitalized(chaptersData.description)}
                        </td>
                        <td className="whitespace-nowrap px-1 py-4 text-sm text-gray-500 border border-black sm:p-xsp sm:text-xs sm:w-6"></td>
                        <td className="px-2 py-1 text-sm text-gray-500 border border-black text-center sm:p-xsp sm:text-xs sm:w-6"></td>
                        <td className="px-2 py-1 text-sm text-gray-500 border border-black text-center max-w-[135px] break-words sm:p-xsp sm:text-xs sm:max-w-12"></td>
                        <td className="px-2 py-1 text-sm text-gray-500 border border-black text-center sm:p-xsp sm:text-xs sm:w-12"></td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              <p className="mt-6 flex items-start gap-xsp text-base sm:text-sm text-baseBlack bg-[#FFFFE0] border border-gray p-2 sm:p-1 rounded-xl">
                <svg
                  xmlns="https://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  width="20"
                  height="20"
                  fill="currentColor"
                  className="mt-0.5 shrink-0"
                  aria-hidden="true"
                >
                  <path d="M12 6C12.8284 6 13.5 5.32843 13.5 4.5C13.5 3.67157 12.8284 3 12 3C11.1716 3 10.5 3.67157 10.5 4.5C10.5 5.32843 11.1716 6 11 6ZM9 10H11V18H9V20H15V18H13V8H9V10Z" />
                </svg>
                <span>
                  <strong className="font-semibold">Disclaimer:</strong>{" "}
                  The HTS code data and duty rates shown on this page are sourced from the
                  Harmonized Tariff Schedule of the United States, Revision 15 (2025),
                  published by the U.S. International Trade Commission (USITC). This
                  information is provided for general reference purposes only. Credlix is a
                  trade finance platform, not a licensed customs broker, freight forwarder,
                  or trade law firm. It makes no warranty, express or implied, regarding the
                  accuracy, completeness, or fitness of this data. Always verify against the
                  latest schedule at{" "}
                  <Link
                    href="https://hts.usitc.gov/"
                    className="text-anchor-blue underline-offset-2"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    hts.usitc.gov
                  </Link>{" "}
                  before making any import decisions.
                </span>
              </p>

               <div className="w-full pt-6">
                 {relatedHTS.length > 1 && (
                   <>
                    <h3 className="font-medium text-lg mb-3">
                      More HTS codes from Chapter {chaptersData.chapter}
                    </h3>
                    <ul className="flex flex-wrap">
                      {relatedHTS.map((data) => (
                        data.htsCode &&
                        <li
                          key={data.htsCode}
                          className="first:pl-0 relative text-sm  after:content-['|'] after:px-2 last:after:content-[''] after:text-sm mb-2"
                        >
                          <Link
                            className="text-secondary hover:text-yellowcolor hover:underline"
                            href={WEB_URL + `/hts-code/${String(data.htsCode).replace(/\./g, "")}`}
                          >
                            HTS Code {data.htsCode}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </div>
            </div>
            <div className="w-[33%] md:w-full sm:w-full">
              <div className="lg:sticky xm:sticky md:mt-5 lg:top-[130px] xm:top-[100px]">
                <FormProvider>
                  {!showNextStep ? (
                    <ContactFormStep1 showStep1={showSteps} />
                  ) : (
                    <ContactFormStep2
                      showPrevStep={showFirstStep}
                      formType="GetInTouchNew"
                    />
                  )}
                </FormProvider>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default htsCode;

export async function getServerSideProps(context) {
  const { res } = context;
  const heading = context.params.htsCode;
  let relatedHTS = [];
  let chaptersData = {};
  let filteredRelatedHTS = [];
  const firstFourDigits = heading.substring(0, 4);

  const locales = context.locales || ['en-in', 'en-us', 'es-mx'];
  if (!heading || heading === "null" || heading === "undefined" || heading === "[htsCode]" || locales.includes(heading)) {
    return { notFound: true };
  }
  
 let options = {
    headers: {
      "Content-Type": "application/json",
    },}

  try {
    const apiRes = await fetch(
      `${ENDPOINTS.HTS_CODE_API}next-hts-codes?htsCode=${heading}&start=0&size=1000`,options,
    );

    if (apiRes.status === 500) {
      console.error("Server error 500 from API");
      res.statusCode = 500;
      return { notFound: true };
    }

    // Handle other failed responses
    if (!apiRes.ok) {
      console.error(`API call failed with status ${apiRes.status}`);
      res.statusCode = apiRes.status;
      return { notFound: true };
    }

    chaptersData = await apiRes.json();

    const relatedMatches =
      chaptersData.chapter?.trim() !== "" ||
      chaptersData.description?.trim() !== "";

    if (!relatedMatches) {
      return { notFound: true };
    }

    const relatedApiRes = await fetch(
      `${ENDPOINTS.HTS_CODE_API}hts-codes?chapter=${chaptersData.chapter}&start=0&size=1000`,options,
    );

    if (relatedApiRes.status === 500) {
      console.error("Server error 500 from related HTS codes API");
      res.statusCode = 500;
      return { notFound: true };
    }
    relatedHTS = await relatedApiRes.json();
    filteredRelatedHTS = relatedHTS.filter(item => item && item.htsCode && !String(item.htsCode).startsWith(firstFourDigits));
  } catch (err) {
    console.error("Error fetching chapter data:", err);
    res.statusCode = 500;
    return { notFound: true };
  }

  return {
    props: {
      id: chaptersData.htsCode,
      chaptersData,
      relatedHTS : filteredRelatedHTS || [],
    },
  };
}
